import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";

export interface PillTab<T extends string> {
  id: T;
  label: string;
}

interface PillTabsProps<T extends string> {
  tabs: PillTab<T>[];
  active: T;
  onChange: (id: T) => void;
  /** Accessible name for the tablist. */
  label: string;
  className?: string;
}

// antd Segmented's own thumb motion (motionDurationSlow + motionEaseInOut), so this
// splitter slides exactly like the app's Segmented controls.
const SLIDE = "0.3s cubic-bezier(0.645, 0.045, 0.355, 1)";

// No focus ring on a splitter tab (mouse or keyboard): the navy pill already shows the selection.
const BASE = "relative z-[1] !h-7 !rounded-full !border-0 !px-4 !text-xs !font-extrabold !transition-colors !duration-300 !outline-none focus-visible:!outline-none";
const ACTIVE = "!text-white hover:!text-white";
const INACTIVE = "!bg-transparent !text-[#0A2947] hover:!bg-[#F3F4F6]";

// The app's pill "splitter". Use this for any tab-like switch instead of antd Tabs.
// The navy pill is a single element that slides under the selected tab.
export function PillTabs<T extends string>({ tabs, active, onChange, label, className }: PillTabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);
  // Off for the first placement, so the pill doesn't slide in from the left on mount.
  const [animate, setAnimate] = useState(false);
  const tabsKey = tabs.map((t) => `${t.id}:${t.label}`).join("|");

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const selected = list.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      setThumb(selected && selected.offsetWidth > 0 ? { left: selected.offsetLeft, width: selected.offsetWidth } : null);
    };
    measure();
    // Labels can change width after mount (web font load, viewport resize).
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [active, tabsKey]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      ref={listRef}
      // One row always: on narrow screens it scrolls sideways instead of wrapping.
      className={cn("msq-no-scrollbar relative inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-[#D1D5DB] bg-white p-1", className)}
      role="tablist"
      aria-label={label}
    >
      {thumb && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-1 bottom-1 rounded-full bg-[#0A2947]"
          style={{
            width: thumb.width,
            transform: `translateX(${thumb.left}px)`,
            transition: animate ? `transform ${SLIDE}, width ${SLIDE}` : "none",
          }}
        />
      )}
      {tabs.map(({ id, label: tabLabel }) => {
        const isActive = active === id;
        return (
          <Button
            key={id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(id)}
            className={cn(
              "shrink-0",
              BASE,
              isActive ? ACTIVE : INACTIVE,
              // Until the pill is measured (or where there's no layout), the
              // selected tab paints its own background.
              isActive && (thumb ? "!bg-transparent hover:!bg-transparent" : "!bg-[#0A2947] hover:!bg-[#0A2947]"),
            )}
          >
            {tabLabel}
          </Button>
        );
      })}
    </div>
  );
}
